import { Button } from '@tovala/component-library'
import {
  useMenuListingsLayout,
  usePublicTermStatuses,
} from '@tovala/browser-apis-combinedapi'
import { ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ErrorCodeMessageMapCombinedAPI, UserTerm } from 'types/internal'

import { useSelectedUserTerm, userTermStatusesQuery } from 'hooks/terms'
import { useUser } from 'contexts/user'
import APIErrorDisplay from 'components/common/APIErrorDisplay'
import CustomWebWidgetLauncher from './CustomWebWidgetLauncher'
import Loader from 'components/common/Loader'
import Menu from './Menu'
import PublicMenu from './PublicMenu'
import { useQuery } from '@tanstack/react-query'

const LOAD_MENU_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please reload the page to try again.',
    whatHappened: 'Unable to Load Menu',
    why: "We couldn't load the menu due to a technical issue on our end.",
  },
}

const MenuPage = ({
  isCurrentTerm,
  onEditTermStatus,
}: {
  isCurrentTerm?: boolean
  onEditTermStatus(userTerm: UserTerm): void
}) => {
  const { termID: termIDParam } = useParams()

  const termIDToView = termIDParam ? Number.parseInt(termIDParam, 10) : null

  const { user } = useUser()

  const isSubscriptionActive = user.subscription.status === 'active'

  return (
    <>
      {isSubscriptionActive ? (
        <LoadActiveSubscriptionMenu
          isCurrentTerm={isCurrentTerm}
          onEditTermStatus={onEditTermStatus}
          termIDToView={termIDToView}
        />
      ) : (
        <LoadPublicMenu
          isCurrentTerm={isCurrentTerm}
          termIDToView={termIDToView}
        />
      )}

      <CustomWebWidgetLauncher />
    </>
  )
}

export default MenuPage

const CenteredContent = ({ children }: { children: ReactNode }) => {
  return (
    <div className="mx-auto my-40 w-full max-w-lg px-6 md:px-4">{children}</div>
  )
}

const UnavailableMenu = () => {
  const navigate = useNavigate()
  return (
    <CenteredContent>
      <div className="text-center">
        <h1 className="text-k/36_110 md:text-k/28_110">
          This menu is unavailable
        </h1>

        <div className="mt-6 flex justify-center">
          <Button onClick={() => navigate('/my-orders')} size="large">
            My Orders
          </Button>
        </div>
      </div>
    </CenteredContent>
  )
}

const LoadPublicMenu = ({
  isCurrentTerm = false,
  termIDToView,
}: {
  isCurrentTerm?: boolean
  termIDToView: number | null
}) => {
  const {
    data: publicTermStatuses = [],
    error: loadPublicTermStatusesError,
    isError: hasLoadPublicTermStatusesError,
    isPending: isLoadingPublicTermStatuses,
  } = usePublicTermStatuses()

  const selectedPublicTerm = isCurrentTerm
    ? publicTermStatuses[0]
    : publicTermStatuses.find((term) => term.termID === termIDToView)

  const menuID = selectedPublicTerm?.subTerms?.length
    ? selectedPublicTerm.subTerms[0].mainMenu.id
    : undefined

  const { data: menuListingsLayout, isPending: isLoadingMenuListingsLayout } =
    useMenuListingsLayout({
      menuID,
    })

  if (selectedPublicTerm && menuListingsLayout) {
    return (
      <PublicMenu
        menuListingsLayout={menuListingsLayout}
        selectedPublicTerm={selectedPublicTerm}
      />
    )
  } else if (hasLoadPublicTermStatusesError) {
    return (
      <CenteredContent>
        <APIErrorDisplay
          display="page"
          error={loadPublicTermStatusesError}
          errorCodeMessageMap={LOAD_MENU_ERRORS}
        />
      </CenteredContent>
    )
  } else if (!isLoadingPublicTermStatuses && !isLoadingMenuListingsLayout) {
    return <UnavailableMenu />
  }

  return <Loader />
}

const LoadActiveSubscriptionMenu = ({
  isCurrentTerm = false,
  onEditTermStatus,
  termIDToView,
}: {
  isCurrentTerm?: boolean
  onEditTermStatus(userTerm: UserTerm): void
  termIDToView: number | null
}) => {
  const { user } = useUser()

  const {
    data: nextTermID,
    error: loadNextTermError,
    isError: hasLoadNextTermError,
    isLoading: isLoadingNextTerm,
  } = useQuery({
    ...userTermStatusesQuery({ user }),
    select: (data) => data[0].termID,
    enabled: isCurrentTerm,
  })

  const isLoadingCurrentTerm = !!(isCurrentTerm && isLoadingNextTerm)

  const {
    error: loadSelectedUserTermError,
    isPending: isLoadingUserTerm,
    selectedUserTerm,
  } = useSelectedUserTerm({
    selectedUserTermID: isCurrentTerm
      ? (nextTermID ?? undefined)
      : (termIDToView ?? undefined),
  })

  const menuID = selectedUserTerm?.selectedSubTerm?.mainMenu.id

  const { data: menuListingsLayout, isPending: isLoadingMenuListingsLayout } =
    useMenuListingsLayout({
      menuID,
    })

  if (isCurrentTerm && !termIDToView && hasLoadNextTermError) {
    return (
      <CenteredContent>
        <APIErrorDisplay
          display="page"
          error={loadNextTermError}
          errorCodeMessageMap={LOAD_MENU_ERRORS}
        />
      </CenteredContent>
    )
  }

  if (selectedUserTerm && menuListingsLayout) {
    return (
      <Menu
        menuListingsLayout={menuListingsLayout}
        onEditTermStatus={onEditTermStatus}
        selectedUserTerm={selectedUserTerm}
      />
    )
  } else if (loadSelectedUserTermError) {
    return (
      <CenteredContent>
        <APIErrorDisplay
          display="page"
          error={loadSelectedUserTermError}
          errorCodeMessageMap={LOAD_MENU_ERRORS}
        />
      </CenteredContent>
    )
  } else if (
    !isLoadingUserTerm &&
    !isLoadingCurrentTerm &&
    (!menuID || !isLoadingMenuListingsLayout)
  ) {
    return <UnavailableMenu />
  }

  return <Loader />
}
